/**
 * image-compressor / png.js — a PNG encoder that actually competes.
 *
 * WHY NOT canvas.toBlob('image/png')
 * Because it is the worst PNG writer on the machine. It picks no row filter
 * worth the name, never considers a palette, and always writes four channels
 * even when the image has no transparency at all. On the corpus's real 2560×1600
 * screenshot that is the difference between handing back 800 KB and handing back
 * something a fifth of the size — with IDENTICAL pixels, because none of this is
 * lossy.
 *
 * That matters more than it sounds: the lossless lane is what lets the tool
 * honour «сжать без потери качества» literally rather than by argument, and it
 * is the right answer for the entire `graphic` family — screenshots, logos,
 * charts, UI — where every lossy codec both looks worse AND, past 256 colours or
 * so, loses to a palette on size as well.
 *
 * WHAT IT DOES
 *   • drops the alpha channel when nothing is transparent (colour type 2);
 *   • picks a row filter per scanline by the standard minimum-sum heuristic;
 *   • quantises to a palette (colour type 3 + tRNS) when the picture can hold
 *     one, which is where the large wins are;
 *   • tries the candidates and returns the SMALLEST — the encoder does not
 *     predict which will win, it measures.
 *
 * ⚠️ DUPLICATION, ON PURPOSE. public/js/merge-images/png-stream.js already
 * contains a CRC table, chunk writer and the same filter heuristic, and it is
 * well tested. It cannot be reused here: it is an ES module, and this file has
 * to be loadable with importScripts inside the CLASSIC worker that mozjpeg's
 * Emscripten glue requires. Sharing it would mean either a second worker hop per
 * encode or 'unsafe-eval' in the tool's CSP. The shared parts are ~80 lines and
 * both copies are verified against a real decoder, so the risk is bounded and
 * visible rather than hidden.
 *
 * Every test decodes the output with sharp (libvips/libpng) rather than with
 * this file's own reader — an encoder only its author can parse is worthless.
 */

(function (global) {
'use strict';

const SIG = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

const CRC_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        t[n] = c >>> 0;
    }
    return t;
})();

function crc32(bytes) {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return c >>> 0;
}

function chunk(type, data) {
    const len = data ? data.length : 0;
    const out = new Uint8Array(12 + len);
    const dv = new DataView(out.buffer);
    dv.setUint32(0, len);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    if (len) out.set(data, 8);
    dv.setUint32(8 + len, crc32(out.subarray(4, 8 + len)) ^ 0xffffffff);
    return out;
}

function ihdr(w, h, colorType, depth) {
    const d = new Uint8Array(13);
    const dv = new DataView(d.buffer);
    dv.setUint32(0, w); dv.setUint32(4, h);
    d[8] = depth || 8; d[9] = colorType;
    d[10] = 0; d[11] = 0; d[12] = 0;
    return chunk('IHDR', d);
}

async function deflate(bytes) {
    if (typeof CompressionStream !== 'function') {
        const e = new Error('CompressionStream unavailable');
        e.code = 'NO_COMPRESSION_STREAM';
        throw e;
    }
    const cs = new CompressionStream('deflate');
    const stream = new Blob([bytes]).stream().pipeThrough(cs);
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

function assemble(w, h, colorType, idat, extra, depth) {
    const parts = [SIG, ihdr(w, h, colorType, depth)];
    if (extra) for (const e of extra) parts.push(e);
    parts.push(chunk('IDAT', idat), chunk('IEND', null));
    let n = 0; for (const p of parts) n += p.length;
    const out = new Uint8Array(n);
    let o = 0; for (const p of parts) { out.set(p, o); o += p.length; }
    return out;
}

/* ── scanline filtering ──────────────────────────────────────────────────── */

function paeth(a, b, c) {
    const p = a + b - c;
    const pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c);
    return (pa <= pb && pa <= pc) ? a : (pb <= pc ? b : c);
}

/**
 * Filter every row with the minimum-sum-of-absolute-differences heuristic.
 * Worth 2-4× on screenshots, where long runs of identical pixels become long
 * runs of zeros, and it is the single biggest reason canvas's PNG is so poor.
 */
function filterRows(raw, w, h, bpp) {
    const stride = w * bpp;
    const out = new Uint8Array((stride + 1) * h);
    let prev = new Uint8Array(stride);

    for (let y = 0; y < h; y++) {
        const row = raw.subarray(y * stride, (y + 1) * stride);
        // Pass 1 scores the five candidates with arithmetic only. Materialising
        // all five into buffers as they are scored — the obvious way to write
        // this — does five stores per byte and then throws four of them away;
        // on a 4 MP RGBA image that is 64 MB of pointless writes per lane.
        let sNone = 0, sSub = 0, sUp = 0, sAvg = 0, sPae = 0;
        for (let i = 0; i < stride; i++) {
            const a = i >= bpp ? row[i - bpp] : 0;
            const b = prev[i];
            const c = i >= bpp ? prev[i - bpp] : 0;
            const v = row[i];
            const vs = (v - a) & 0xff, vu = (v - b) & 0xff;
            const va = (v - ((a + b) >> 1)) & 0xff, vp = (v - paeth(a, b, c)) & 0xff;
            sNone += v < 128 ? v : 256 - v;
            sSub += vs < 128 ? vs : 256 - vs;
            sUp += vu < 128 ? vu : 256 - vu;
            sAvg += va < 128 ? va : 256 - va;
            sPae += vp < 128 ? vp : 256 - vp;
        }
        let type = 0, best = sNone;
        if (sSub < best) { best = sSub; type = 1; }
        if (sUp < best) { best = sUp; type = 2; }
        if (sAvg < best) { best = sAvg; type = 3; }
        if (sPae < best) { best = sPae; type = 4; }

        // Pass 2 writes only the winner, straight into the output stream.
        const o = y * (stride + 1);
        out[o] = type;
        if (type === 0) {
            out.set(row, o + 1);
        } else {
            for (let i = 0; i < stride; i++) {
                const a = i >= bpp ? row[i - bpp] : 0;
                const b = prev[i];
                const v = row[i];
                let f;
                if (type === 1) f = v - a;
                else if (type === 2) f = v - b;
                else if (type === 3) f = v - ((a + b) >> 1);
                else f = v - paeth(a, b, i >= bpp ? prev[i - bpp] : 0);
                out[o + 1 + i] = f & 0xff;
            }
        }
        prev = row;
    }
    return out;
}

/* ── palette quantisation ────────────────────────────────────────────────── */

/**
 * The EXACT distinct colours, or null once there are more than `cap`.
 *
 * ⚠️ This exists because the first version of this file reported `lossless:
 * true` for palettes that were nothing of the kind. The quantiser bins colours
 * into 5-bit cells and averages each cell, so "the image has fewer cells than
 * palette slots" says nothing about whether the pixels survive — and measured
 * against a real decoder, those supposedly lossless outputs moved pixels by up
 * to 123 levels. A tool whose whole promise is «без потери качества» cannot
 * decide losslessness from an approximation of the colour count.
 *
 * Losslessness is now decided here, on full-precision RGBA, and a palette built
 * from these entries reproduces every pixel bit-for-bit by construction.
 */
function exactPalette(rgba, n, cap) {
    const limit = cap || 256;
    const seen = new Map();
    for (let i = 0, p = 0; i < n; i++, p += 4) {
        const key = (rgba[p] << 24 | rgba[p + 1] << 16 | rgba[p + 2] << 8 | rgba[p + 3]) >>> 0;
        if (!seen.has(key)) {
            if (seen.size >= limit) return null;
            seen.set(key, [rgba[p], rgba[p + 1], rgba[p + 2], rgba[p + 3]]);
        }
    }
    return Array.from(seen.values());
}

/** Index every pixel against an EXACT palette — a lookup, never a search. */
function mapExact(rgba, n, palette) {
    const lut = new Map();
    for (let i = 0; i < palette.length; i++) {
        const p = palette[i];
        lut.set((p[0] << 24 | p[1] << 16 | p[2] << 8 | p[3]) >>> 0, i);
    }
    const idx = new Uint8Array(n);
    for (let i = 0, p = 0; i < n; i++, p += 4) {
        idx[i] = lut.get((rgba[p] << 24 | rgba[p + 1] << 16 | rgba[p + 2] << 8 | rgba[p + 3]) >>> 0);
    }
    return idx;
}

/**
 * Median cut over RGBA.
 *
 * Alpha participates in the split rather than being handled separately: a logo
 * whose only variation is its edge opacity needs its palette spent on alpha
 * steps, and a quantiser that splits on colour alone gives it one entry and a
 * hard, jagged edge.
 */
function quantize(rgba, w, h, maxColors) {
    const n = w * h;
    // 5 bits per channel: 32768 colour cells, times an alpha bucket. Fine enough
    // that no two visibly different colours share a cell, coarse enough to build
    // in one pass over a 12 MP image.
    const HIST = new Uint32Array(32768);
    const AR = new Float64Array(32768), AG = new Float64Array(32768);
    const AB = new Float64Array(32768), AA = new Float64Array(32768);
    for (let i = 0, p = 0; i < n; i++, p += 4) {
        const key = ((rgba[p] >> 3) << 10) | ((rgba[p + 1] >> 3) << 5) | (rgba[p + 2] >> 3);
        HIST[key]++;
        AR[key] += rgba[p]; AG[key] += rgba[p + 1]; AB[key] += rgba[p + 2]; AA[key] += rgba[p + 3];
    }
    const cells = [];
    for (let k = 0; k < 32768; k++) {
        if (!HIST[k]) continue;
        const c = HIST[k];
        cells.push({
            r: AR[k] / c, g: AG[k] / c, b: AB[k] / c, a: AA[k] / c, n: c,
        });
    }
    if (!cells.length) return null;
    if (cells.length <= maxColors) {
        return { palette: cells.map(c => [Math.round(c.r), Math.round(c.g), Math.round(c.b), Math.round(c.a)]), exact: true };
    }

    let boxes = [cells];
    while (boxes.length < maxColors) {
        // Split the box with the largest weighted spread — that is where the
        // visible error is, not necessarily where the most pixels are.
        let bi = -1, bScore = -1, bAxis = 'r';
        for (let i = 0; i < boxes.length; i++) {
            const box = boxes[i];
            if (box.length < 2) continue;
            let lo = [255, 255, 255, 255], hi = [0, 0, 0, 0], count = 0;
            for (const c of box) {
                const v = [c.r, c.g, c.b, c.a];
                for (let k = 0; k < 4; k++) { if (v[k] < lo[k]) lo[k] = v[k]; if (v[k] > hi[k]) hi[k] = v[k]; }
                count += c.n;
            }
            // Luma-weighted: the eye resolves green far better than blue, and
            // alpha steps are as visible as luma ones.
            const spread = [(hi[0] - lo[0]) * 0.30, (hi[1] - lo[1]) * 0.59, (hi[2] - lo[2]) * 0.11, (hi[3] - lo[3]) * 0.50];
            let axis = 0;
            for (let k = 1; k < 4; k++) if (spread[k] > spread[axis]) axis = k;
            const score = spread[axis] * Math.log(1 + count);
            if (score > bScore) { bScore = score; bi = i; bAxis = ['r', 'g', 'b', 'a'][axis]; }
        }
        if (bi < 0) break;
        const box = boxes[bi];
        box.sort((x, y) => x[bAxis] - y[bAxis]);
        let total = 0; for (const c of box) total += c.n;
        let acc = 0, cut = 1;
        for (let i = 0; i < box.length - 1; i++) { acc += box[i].n; if (acc >= total / 2) { cut = i + 1; break; } }
        boxes.splice(bi, 1, box.slice(0, cut), box.slice(cut));
    }

    const palette = boxes.map(box => {
        let r = 0, g = 0, b = 0, a = 0, t = 0;
        for (const c of box) { r += c.r * c.n; g += c.g * c.n; b += c.b * c.n; a += c.a * c.n; t += c.n; }
        return [Math.round(r / t), Math.round(g / t), Math.round(b / t), Math.round(a / t)];
    });
    return { palette, exact: false };
}

/** Map every pixel to its nearest palette entry, through a cache keyed on the
 *  5-bit cell so the 256-entry scan runs once per distinct colour, not per
 *  pixel. Optional Floyd–Steinberg error diffusion. */
function mapToPalette(rgba, w, h, palette, dither) {
    const n = w * h;
    const idx = new Uint8Array(n);
    const cache = new Int16Array(32768 * 8).fill(-1);
    const P = palette.length;
    const nearest = (r, g, b, a) => {
        let best = 0, bd = Infinity;
        for (let i = 0; i < P; i++) {
            const p = palette[i];
            const dr = r - p[0], dg = g - p[1], db = b - p[2], da = a - p[3];
            // Same channel weighting as the split, plus alpha: a wrong opacity
            // is as visible as a wrong hue.
            const d = dr * dr * 0.30 + dg * dg * 0.59 + db * db * 0.11 + da * da * 0.5;
            if (d < bd) { bd = d; best = i; }
        }
        return best;
    };

    if (!dither) {
        for (let i = 0, p = 0; i < n; i++, p += 4) {
            const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2], a = rgba[p + 3];
            const key = (((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3)) * 8 + (a >> 5);
            let v = cache[key];
            if (v < 0) { v = nearest(r, g, b, a); cache[key] = v; }
            idx[i] = v;
        }
        return idx;
    }

    // Error diffusion needs float working rows; only two are alive at a time.
    const cur = new Float32Array(w * 4), nxt = new Float32Array(w * 4);
    for (let x = 0; x < w * 4; x++) cur[x] = rgba[x];
    for (let y = 0; y < h; y++) {
        nxt.fill(0);
        if (y + 1 < h) {
            const off = (y + 1) * w * 4;
            for (let x = 0; x < w * 4; x++) nxt[x] = rgba[off + x];
        }
        for (let x = 0; x < w; x++) {
            const o = x * 4;
            const r = Math.max(0, Math.min(255, cur[o]));
            const g = Math.max(0, Math.min(255, cur[o + 1]));
            const b = Math.max(0, Math.min(255, cur[o + 2]));
            const a = Math.max(0, Math.min(255, cur[o + 3]));
            const v = nearest(r, g, b, a);
            idx[y * w + x] = v;
            const p = palette[v];
            const er = r - p[0], eg = g - p[1], eb = b - p[2], ea = a - p[3];
            if (x + 1 < w) {
                cur[o + 4] += er * 7 / 16; cur[o + 5] += eg * 7 / 16;
                cur[o + 6] += eb * 7 / 16; cur[o + 7] += ea * 7 / 16;
            }
            if (y + 1 < h) {
                if (x > 0) {
                    nxt[o - 4] += er * 3 / 16; nxt[o - 3] += eg * 3 / 16;
                    nxt[o - 2] += eb * 3 / 16; nxt[o - 1] += ea * 3 / 16;
                }
                nxt[o] += er * 5 / 16; nxt[o + 1] += eg * 5 / 16;
                nxt[o + 2] += eb * 5 / 16; nxt[o + 3] += ea * 5 / 16;
                if (x + 1 < w) {
                    nxt[o + 4] += er / 16; nxt[o + 5] += eg / 16;
                    nxt[o + 6] += eb / 16; nxt[o + 7] += ea / 16;
                }
            }
        }
        cur.set(nxt);
    }
    return idx;
}

/* ── encoders ────────────────────────────────────────────────────────────── */

function hasAlpha(rgba, n) {
    for (let i = 3; i < n * 4; i += 4) if (rgba[i] !== 255) return true;
    return false;
}

/** Every row filtered as type 0 (None) — the raw bytes with a zero in front. */
function filterNone(raw, w, h, bpp) {
    const stride = w * bpp;
    const out = new Uint8Array((stride + 1) * h);
    for (let y = 0; y < h; y++) {
        out[y * (stride + 1)] = 0;
        out.set(raw.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1);
    }
    return out;
}

/**
 * Truecolour PNG, with BOTH filtering strategies tried and the smaller kept.
 *
 * ⚠️ THE CLASSIC HEURISTIC IS WRONG HALF THE TIME, and by a lot. The
 * minimum-sum-of-absolute-differences rule that every PNG writer uses optimises
 * for the ENTROPY CODER — small residuals — while completely ignoring what
 * deflate actually does first, which is look for repeated byte runs. On a
 * screenshot, long runs of identical pixels are exactly what LZ77 lives on, and
 * filtering turns each run into a different residual pattern that breaks the
 * matches.
 *
 * Measured on the corpus, lossless RGB(A), zlib level 9:
 *
 *      screenshotUi      762 KB adaptive  →  555 KB none      −27%
 *      screenshotDrawn   141 KB           →  113 KB           −20%
 *      logoAlpha          30 KB           →   26 KB           −14%
 *      redTextWhite       57 KB           →   49 KB           −14%
 *      photoNature      1251 KB           → 2110 KB           +69%
 *      photoPortrait     728 KB           → 1452 KB           +99%
 *      transparentPhoto 1071 KB           → 1672 KB           +56%
 *
 * Neither answer is right; the split is almost exactly graphics vs photographs.
 * Guessing which side an image falls on is a classifier, and a classifier can be
 * wrong — deflating twice cannot. The second pass costs one more deflate of an
 * already-built stream, which is the cheap half of this function.
 */
async function encodeTruecolor(rgba, w, h, keepAlpha) {
    const bpp = keepAlpha ? 4 : 3;
    const stride = w * bpp;
    let raw;
    if (keepAlpha) {
        raw = rgba.length === w * h * 4 ? rgba : rgba.subarray(0, w * h * 4);
    } else {
        raw = new Uint8Array(stride * h);
        for (let i = 0, o = 0, p = 0; i < w * h; i++, o += 3, p += 4) {
            raw[o] = rgba[p]; raw[o + 1] = rgba[p + 1]; raw[o + 2] = rgba[p + 2];
        }
    }
    const colorType = keepAlpha ? 6 : 2;
    const [adaptive, none] = await Promise.all([
        deflate(filterRows(raw, w, h, bpp)),
        deflate(filterNone(raw, w, h, bpp)),
    ]);
    const best = none.length < adaptive.length ? none : adaptive;
    const out = assemble(w, h, colorType, best);
    out.__filter = none.length < adaptive.length ? 'none' : 'adaptive';
    return out;
}

async function encodePalette(idx, palette, w, h) {
    // Order entries so every fully-opaque one sits at the end: tRNS is a prefix
    // of the palette by specification, so sorting by alpha makes the chunk as
    // short as it can be, and lets it vanish entirely when nothing is
    // transparent.
    const order = palette.map((p, i) => i).sort((a, b) => palette[a][3] - palette[b][3]);
    const remap = new Uint8Array(palette.length);
    order.forEach((old, nu) => { remap[old] = nu; });
    const sorted = order.map(i => palette[i]);

    const plte = new Uint8Array(sorted.length * 3);
    for (let i = 0; i < sorted.length; i++) {
        plte[i * 3] = sorted[i][0]; plte[i * 3 + 1] = sorted[i][1]; plte[i * 3 + 2] = sorted[i][2];
    }
    let trnsLen = 0;
    for (let i = 0; i < sorted.length; i++) if (sorted[i][3] < 255) trnsLen = i + 1;
    const extra = [chunk('PLTE', plte)];
    if (trnsLen) {
        const trns = new Uint8Array(trnsLen);
        for (let i = 0; i < trnsLen; i++) trns[i] = sorted[i][3];
        extra.push(chunk('tRNS', trns));
    }

    const raw = new Uint8Array(w * h);
    for (let i = 0; i < raw.length; i++) raw[i] = remap[idx[i]];
    // Same two-strategy race as the truecolour lane: palette indices are one
    // byte per pixel, so flat regions are long identical runs and filtering
    // them is usually a loss.
    const [adaptive, none] = await Promise.all([
        deflate(filterRows(raw, w, h, 1)),
        deflate(filterNone(raw, w, h, 1)),
    ]);
    return assemble(w, h, 3, none.length < adaptive.length ? none : adaptive, extra);
}

/**
 * Encode, trying what might win and returning the smallest.
 *
 * `maxColors` 0 disables the palette lane. `dither` diffuses quantisation error
 * — it costs bytes (noise compresses badly) and is only worth it on gradients,
 * so callers pass it deliberately rather than getting it by default.
 *
 * Returns { bytes, kind, colors } so the report can say WHAT it did — "palette,
 * 128 colours" is a fact a user can check, "optimised" is not.
 */
async function encode(rgba, w, h, opts) {
    const o = opts || {};
    const n = w * h;
    const alpha = o.forceAlpha !== undefined ? o.forceAlpha : hasAlpha(rgba, n);
    const maxColors = o.maxColors === undefined ? 256 : o.maxColors;
    const wantLossy = o.allowLossyPalette === true;
    const candidates = [];

    /* The lossless palette first, because when it exists it is both smaller and
     * pixel-perfect, and knowing that lets the truecolour lane be skipped
     * entirely on the images where it would only be a slower way to lose. */
    let exact = null;
    if (maxColors >= 2) exact = exactPalette(rgba, n, Math.min(256, maxColors));
    if (exact && exact.length) {
        const bytes = await encodePalette(mapExact(rgba, n, exact), exact, w, h);
        candidates.push({ bytes, kind: 'palette', colors: exact.length, lossless: true });
    }

    if (!exact || !o.skipTruecolorWhenExact) {
        const truecolor = await encodeTruecolor(rgba, w, h, alpha);
        candidates.push({ bytes: truecolor, kind: alpha ? 'rgba' : 'rgb', colors: null, lossless: true });
    }

    /* A CUT palette changes pixels, so it is only built when the caller has said
     * it wants a lossy candidate — and it is returned flagged, for the metric to
     * judge like any other lossy encode. It must never be handed back as part of
     * a lossless answer just because it happens to be the smallest file. */
    if (wantLossy && !exact && maxColors >= 2 && n <= (o.paletteMaxPixels || 40e6)) {
        const q = quantize(rgba, w, h, maxColors);
        if (q && q.palette.length) {
            const idx = mapToPalette(rgba, w, h, q.palette, !!o.dither);
            const bytes = await encodePalette(idx, q.palette, w, h);
            candidates.push({ bytes, kind: 'palette-lossy', colors: q.palette.length, lossless: false });
        }
    }

    // Smallest LOSSLESS candidate wins by default; a lossy one is only returned
    // when it was asked for and nothing lossless was produced.
    const lossless = candidates.filter(c => c.lossless);
    const pool = lossless.length ? lossless : candidates;
    pool.sort((a, b) => a.bytes.length - b.bytes.length);
    const best = pool[0];
    const lossy = candidates.filter(c => !c.lossless).sort((a, b) => a.bytes.length - b.bytes.length)[0];

    return {
        bytes: best.bytes, kind: best.kind, colors: best.colors, lossless: best.lossless,
        // Offered separately so the pipeline can measure it and decide, rather
        // than have this function silently trade fidelity for bytes.
        lossy: lossy ? { bytes: lossy.bytes, kind: lossy.kind, colors: lossy.colors } : null,
        tried: candidates.map(c => ({ kind: c.kind, size: c.bytes.length, lossless: c.lossless })),
    };
}

const API = { encode, encodeTruecolor, encodePalette, quantize, mapToPalette, filterNone,
              exactPalette, mapExact, filterRows, hasAlpha, crc32, deflate };

global.__icPng = API;
})(typeof self !== 'undefined' ? self : this);
